import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AdvancedFilterModel, IFilterParams } from 'ag-grid-enterprise';
import { Subject, debounceTime, filter } from 'rxjs';

import { InputChoicesComponent, TextBoxComponent } from '@camelot/form-input';
import { TranslatePipe } from '@camelot/translation';
import { TextComponent } from '@camelot/ui';

import { ActiveFilterTag } from '../../../../filters/types';
import { BaseFilterComponent, operatorMap } from '../base-col';
import type { FilterTyping, StringCol } from './string-col';

export const STRING_FILTER_DEBOUNCE_MS = 500;
export const STRING_FILTER_MIN_LENGTH = 3;

@Component({
  host: { 'data-cid': 'string-filter' },
  imports: [InputChoicesComponent, TextBoxComponent, TextComponent, TranslatePipe],
  styleUrls: ['../filter.scss'],
  template: `
    @if (this.input) {
      <div>
        <div class="filter-title">
          <cam-text [isBold]="true" size="sm" color="brand-primary">{{ this.filterTitle() | translate }}</cam-text>
        </div>
        <div class="p-space-sm">
          @if (this.isMultiSearch) {
            <cam-input-choices
              [input]="$any(this.input)"
              [standalone]="true"
              (valueChanged)="this.applyInputValue($event)"
            ></cam-input-choices>
          }
          @if (!this.isMultiSearch) {
            <cam-input-textbox
              [input]="$any(this.input)"
              [standalone]="true"
              [onFocus]="this.focus$"
              (valueChanged)="this.search$.next($any($event))"
            ></cam-input-textbox>
          }
        </div>
      </div>
    }
  `,
})
export class StringFilterComponent extends BaseFilterComponent<FilterTyping, StringCol> {
  public readonly search$ = new Subject<string>();

  get isMultiSearch() {
    return this._col?.data.col.multiSearchEnabled;
  }

  constructor() {
    super();
    this.search$
      .pipe(
        filter(() => !this._suppressApply),
        debounceTime(STRING_FILTER_DEBOUNCE_MS),
        filter(value => !value || value.trim().length >= STRING_FILTER_MIN_LENGTH),
        takeUntilDestroyed()
      )
      .subscribe(value => this.applyInputValue(value));
  }

  override agInit(params: IFilterParams) {
    super.agInit(params);
  }

  override getTags(model?: AdvancedFilterModel | null): ActiveFilterTag[] {
    if (!model) {
      model = this._model;
    }
    if (!model) {
      return [];
    }
    if (model.filterType === 'text') {
      if (model.filter === undefined) {
        return [];
      }

      const operator = operatorMap[model.type] || model.type;

      return [
        {
          condition: model,
          id: model.colId,
          name: `${operator} ${model.filter}`,
        },
      ];
    }
    if (model.filterType === 'join') {
      return model.conditions.flatMap(condition => this.getTags(condition));
    }

    return [];
  }
}
